import { authStore } from "./authStore";
import { performRefresh, refreshWhileActive } from "./refreshSession";

const SESSION_ERROR_STATUS = [419, 498];

function isAuthEndpoint(url) {
  if (!url) {
    return false;
  }
  return (
    url.includes("auth/refresh") ||
    url.includes("auth/login") ||
    url.includes("auth/admin/login")
  );
}

/**
 * Attach sliding-session interceptors to an axios instance.
 * - Sends refresh cookie via withCredentials (must be set on the instance)
 * - Proactively refreshes near-expiry portal tokens before each request
 * - Retries once on 401 after refresh
 */
export function attachAuthInterceptors(client) {
  client.interceptors.request.use(async (config) => {
    // Idle popup owns the session — do not refresh or reset idle from background APIs.
    if (!authStore.isIdleDialogPending()) {
      authStore.recordActivity();
      await refreshWhileActive("request");
    }

    const token = authStore.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      const status = error.response?.status;

      if (
        status === 401 &&
        originalRequest &&
        !originalRequest._retry &&
        !isAuthEndpoint(originalRequest.url) &&
        authStore.isPortalSession() &&
        authStore.usesRefreshCookie()
      ) {
        originalRequest._retry = true;

        // Let the idle dialog countdown finish — do not bounce to /sign-in.
        if (authStore.isIdleDialogPending()) {
          return Promise.reject(error);
        }

        try {
          await performRefresh();
          const token = authStore.getAccessToken();
          if (token) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return client(originalRequest);
        } catch {
          if (!authStore.isIdleDialogPending()) {
            const { signOutGlobally } = await import("../store/auth/authStore");
            signOutGlobally();
          }
        }
      }

      if (
        status &&
        SESSION_ERROR_STATUS.includes(status) &&
        !authStore.isIdleDialogPending()
      ) {
        import("../store/auth/authStore").then(({ signOutGlobally }) => {
          signOutGlobally();
        });
      }

      return Promise.reject(error);
    },
  );

  return client;
}

export { SESSION_ERROR_STATUS };
